import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import { HelpPopover, PageHeader } from '@/design';
import { formatDateTime } from '@/domains/survey';
import type { IPage } from '../api/oversight-contracts';
import { secAdvApi } from '../api/sec-adv-api';
import type {
  IAdminSecAdvFirmFeed,
  IAdminSecAdvFilings,
  IAdminSecAdvImport,
  IAdminSecAdvJob,
  IAdminSecAdvJobs,
  ISecAdvCatalogueValue
} from '../api/sec-adv-contracts';
import {
  Fact,
  LoadError,
  Pager,
  PastTheEnd,
  RadioFilter,
  SEC_ADV_TABS,
  Section,
  SectionTabs,
  TableSkeleton,
  pageOf,
  useAdminData,
  withPage
} from '../components/admin-page';
import { formatDay, formatMonth } from '../components/days';
import { WorkPageHeaderActions, useReadAt } from './lists/work-page/header-actions';
import { registerSummary } from './sec-adv/register';
import {
  FIRM_TYPE,
  LabelledCode,
  codeValues,
  useSecAdvCatalogue
} from '../components/sec-adv-values';
import { refusalMessage } from '../refusal';

/**
 * The SEC ADV register's own state — `GET /api/v1/admin/sec-adv/status` and
 * `GET …/sec-adv/imports` (survey-api's SR-214), under `registry.browse`, so staff and admin
 * alike. Whether the register the worker imports can answer a lookup today, and what its
 * imports did: the daily feed, the monthly filing window, the latest import jobs, and the log
 * of every file read.
 *
 * **Every member is said in the server's words, and nothing is judged here.** `fresh` is
 * survey-api's rule — the latest feed's own date against `maxFeedAge`, the rule every SEC ADV
 * lookup is refused by (a feed six days old answers, one seven days old does not) — so it is
 * said as that rule, "less than 7 days old" or "7 days old or more: every lookup answers
 * `error`", and never as "healthy": a fresh feed that imported no firms still answers every
 * lookup `error`, which the firm count beside it shows. A job's
 * status is said as survey-api's queue has it, with the two readings it gives no word of its
 * own: `queued` after a failed attempt is a retry coming, and `dead` is out of attempts and
 * never retried by itself — the one state here that needs somebody. A firm type in the counts is
 * labelled through the catalogue, with the feed's spelling beside it, as on the firm card and the
 * person hub (the owner's ruling, 2026-09-25), and is the feed's spelling alone without it.
 *
 * **Dates with no time are days, instants are instants.** A feed's date, a month of the window
 * and the window's start name days on the SEC's calendar and are drawn as sent (`days.ts`);
 * when an import ran and a job's times are instants, through `formatDateTime`. `maxFeedAge` is a
 * .NET `TimeSpan`, drawn in whole days where it reads so and as sent where it does not.
 *
 * **Two reads, each on its own.** The state and the log load and fail apart, so a hand-made
 * `?kind=` survey-api refuses is the log's error in survey-api's own words — its `problems[]`,
 * which say which value was wrong, rather than the title that says only that one was — and the
 * three blocks above it still stand. A re-read of either that fails leaves what it last read on
 * screen, under "Could not refresh", never a first load's banner. An empty page of a log that is
 * not empty says it is past the last page; an answer with no import type at all says that.
 *
 * **The log's filter and page are the URL**, as the registrations queue's are, so a log can be
 * pasted. A `kind` this build has no position for is still sent, and survey-api's 400 says what
 * is wrong with it.
 *
 * **Nothing here polls, and nothing here writes.** The person hub stays the one screen that
 * reads itself again; this one is read when opened, and again on *Refresh*, quietly —
 * what is on screen stays until the next answer lands — since a job may be running. There is no
 * "import now": survey-api has no such route, and a button that looked like one would be the lie.
 */
export function AdminSecAdvStatusPage() {
  const [params, setParams] = useSearchParams();
  const kind = params.get('kind') ?? '';
  const page = pageOf(params.get('page'));

  const state = useAdminData((signal) => secAdvApi.getStatus(signal));
  // Labels a firm type in the counts, and nothing else here waits on it or fails with it.
  const labels = useSecAdvCatalogue();

  const asked = JSON.stringify([kind, page]);
  const log = useAdminData<IAnsweredLog>(
    async (signal) => {
      try {
        return {
          asked,
          filtered: kind !== '',
          page: await secAdvApi.listImports({ kind, page }, signal)
        };
      } catch (error) {
        throw new Error(refusalMessage(error, 'Could not load the import log'));
      }
    },
    [asked]
  );
  // The last page while the next is out; nothing once this filter's read has failed.
  const shownLog = log.error && log.data?.asked !== asked ? null : log.data;

  const [reading, setReading] = React.useState(false);
  const readAgain = async () => {
    setReading(true);
    await Promise.all([state.reload(), log.reload()]);
    setReading(false);
  };
  const readAt = useReadAt(state.data);

  /** A filter change starts at page 1: page 3 of the old selection means nothing. */
  const chooseKind = (next: string) => {
    const changed = new URLSearchParams(params);
    if (next === '') changed.delete('kind');
    else changed.set('kind', next);
    changed.delete('page');
    setParams(changed);
  };

  return (
    <>
      {/* The Firms tab's header row, so switching tabs moves nothing. */}
      <PageHeader
        title="SEC ADV register"
        row
        tabs={<SectionTabs tabs={SEC_ADV_TABS} />}
        actions={
          <WorkPageHeaderActions
            summary={registerSummary(state.data?.firmFeed)}
            refresh={{ reading, readAt, onClick: () => void readAgain() }}
            help={
              <HelpPopover label="About the register status" align="end">
                <p>
                  The register imported from the SEC: its daily feed of firms, the
                  monthly filing archives that name each firm's custodians, the import jobs, and every
                  file they read — as of today (UTC). Read-only: nothing here imports or retries, and
                  nothing reads itself again.
                </p>
              </HelpPopover>
            }
          />
        }
      />
      <div className="grid gap-6 px-6 py-4">

      {state.error &&
        (state.data === null ? (
          <LoadError message={state.error} />
        ) : (
          <Alert variant="destructive">
            <AlertTitle>Could not refresh — showing the register as last read</AlertTitle>
            <AlertDescription>{state.error}</AlertDescription>
          </Alert>
        ))}

      {state.data === null ? (
        state.error ? null : <TableSkeleton rows={4} />
      ) : (
        <>
          <DailyFeed feed={state.data.firmFeed} typeValues={codeValues(labels.data, FIRM_TYPE)} />
          <Filings filings={state.data.filings} />
          <ImportJobs jobs={state.data.jobs} />
        </>
      )}

      <Section
        title="Import log"
        description="Every file an import has read, the latest date first — the register's history, kept after the queue above has let its jobs go."
      >
        <CardContent className="grid gap-4 text-sm">
          <RadioFilter
            label="Kind"
            name="sec-adv-import-kind"
            options={KINDS}
            value={kind}
            onChoose={chooseKind}
          />

          {log.error &&
            (shownLog === null ? (
              <LoadError message={log.error} />
            ) : (
              // The log as last read stays, as the state above does: a failed re-read says so
              // over it rather than claiming the log never loaded.
              <Alert variant="destructive">
                <AlertTitle>Could not refresh — showing the log as last read</AlertTitle>
                <AlertDescription>{log.error}</AlertDescription>
              </Alert>
            ))}

          {shownLog === null ? (
            log.error ? null : <TableSkeleton rows={3} />
          ) : (
            <>
              <ImportTable
                page={shownLog.page}
                filtered={shownLog.filtered}
                onPage={(target) => setParams(withPage(params, target))}
              />
              <Pager
                page={shownLog.page.page}
                pageSize={shownLog.page.pageSize}
                total={shownLog.page.total}
                onPage={(target) => setParams(withPage(params, target))}
              />
            </>
          )}
        </CardContent>
      </Section>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// The daily feed
// ---------------------------------------------------------------------------

function DailyFeed({
  feed,
  typeValues
}: {
  feed: IAdminSecAdvFirmFeed;
  /** The catalogue's values for a firm's type — `[]` until it lands, or for good without it. */
  typeValues: ISecAdvCatalogueValue[];
}) {
  const age = feedAge(feed.maxFeedAge);
  return (
    <Section
      title="Daily feed"
      description="The SEC's file of every firm it registers or takes reports from, one a day. The register holds the latest."
    >
      <CardContent className="grid gap-4 text-sm">
        <Freshness feed={feed} age={age} />

        {feed.latest && (
          <div className="grid gap-3 sm:grid-cols-3">
            <ImportFacts entry={feed.latest} />
          </div>
        )}

        <Fact
          label="Firms held"
          value={
            feed.firmTypes.length === 0 ? (
              'The register holds no firms.'
            ) : (
              <ul className="grid gap-0.5">
                {feed.firmTypes.map((count) => (
                  <li key={count.firmType}>
                    <LabelledCode code={count.firmType} values={typeValues} />: {count.firms}{' '}
                    {count.firms === 1 ? 'firm' : 'firms'}
                  </li>
                ))}
              </ul>
            )
          }
        />
      </CardContent>
    </Section>
  );
}

/**
 * `fresh`, said as the rule it is. Loud when it is false, because then every SEC ADV lookup —
 * a registration's, a response's prefill, a card's preview — answers `error`; never called
 * "healthy" when it is true, because it reads the feed's date and nothing else.
 */
function Freshness({ feed, age }: { feed: IAdminSecAdvFirmFeed; age: React.ReactNode }) {
  if (feed.fresh) {
    return (
      <Alert>
        <AlertTitle>The latest feed is less than {age} old</AlertTitle>
        <AlertDescription>
          An SEC ADV lookup is answered only from a feed less than {age} old, by the
          feed's own date. Whether that feed held any firms is the count below.
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <Alert variant="destructive">
      <AlertTitle>
        {feed.latest ? (
          <>The latest feed is {age} old or more</>
        ) : (
          'No firm feed has been imported'
        )}
      </AlertTitle>
      <AlertDescription>
        <p>
          Every SEC ADV lookup answers <code>error</code> until a feed less than {age} old is
          imported.
        </p>
      </AlertDescription>
    </Alert>
  );
}

/** One import's facts — the file, the date it describes, what it read and when it ran. */
function ImportFacts({ entry }: { entry: IAdminSecAdvImport }) {
  return (
    <>
      <Fact label="File" value={<span className="font-mono text-xs">{entry.fileName}</span>} />
      <Fact label="Describes" value={formatDay(entry.generatedOn)} />
      <Fact label="Imported" value={formatDateTime(entry.importedAt)} />
      <Fact label="Rows read" value={entry.rowCount} />
      <Fact label="Rows skipped" value={entry.skippedCount} />
    </>
  );
}

/**
 * `maxFeedAge` in whole days where its .NET `TimeSpan` spelling — `[d.]hh:mm:ss[.fffffff]` —
 * is exactly that, and as sent where it is anything else: `7.12:00:00` is no number of days,
 * and rounding it would state a rule survey-api does not apply.
 */
function feedAge(span: string): React.ReactNode {
  const [days, time, ...rest] = span.split('.');
  const whole = Number.parseInt(days, 10);
  if (rest.length > 0 || time !== '00:00:00' || String(whole) !== days) return <code>{span}</code>;
  return whole === 1 ? '1 day' : `${whole} days`;
}

// ---------------------------------------------------------------------------
// The filing window
// ---------------------------------------------------------------------------

function Filings({ filings }: { filings: IAdminSecAdvFilings }) {
  return (
    <Section
      title="Filings"
      description="The SEC's monthly archives of Form ADV filings, read for each firm's custodians (Schedule D 5.K(3)). A lookup uses a filing only inside the window."
    >
      <CardContent className="grid gap-4 text-sm">
        <div className="grid gap-3 sm:grid-cols-4">
          <Fact label="Window starts" value={formatDay(filings.windowStart)} />
          <Fact
            label="Window length"
            value={`${filings.windowMonths} ${filings.windowMonths === 1 ? 'month' : 'months'}, ending with last month`}
          />
          <Fact label="Custodian rows held" value={filings.custodianRows} />
          <Fact label="Inside the window" value={filings.custodianRowsInWindow} />
        </div>

        <div className="overflow-x-auto rounded-lg border border-border">
          <Table aria-label="Months of the window">
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead>Archive</TableHead>
                <TableHead>Rows read</TableHead>
                <TableHead>Rows skipped</TableHead>
                <TableHead>Imported</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filings.months.map((month) => (
                <TableRow key={month.month}>
                  <TableCell className="whitespace-nowrap">{formatMonth(month.month)}</TableCell>
                  {month.import ? (
                    <>
                      <TableCell className="font-mono text-xs">{month.import.fileName}</TableCell>
                      <TableCell>{month.import.rowCount}</TableCell>
                      <TableCell>{month.import.skippedCount}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                        {formatDateTime(month.import.importedAt)}
                      </TableCell>
                    </>
                  ) : (
                    <TableCell colSpan={4} className="text-muted-foreground">
                      Not imported
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// The import jobs
// ---------------------------------------------------------------------------

function ImportJobs({ jobs }: { jobs: IAdminSecAdvJobs[] }) {
  return (
    <Section
      title="Import jobs"
      description="The latest ten jobs of each import type on the import queue, newest first. The queue keeps a finished job only for a while; the import log below is the history."
    >
      <CardContent className="grid gap-6 text-sm">
        {jobs.length === 0 ? (
          <p className="text-muted-foreground">
            No import type was listed, so nothing here says what the queue holds.
          </p>
        ) : (
          jobs.map((group) => <JobGroup key={group.jobType} group={group} />)
        )}
      </CardContent>
    </Section>
  );
}

function JobGroup({ group }: { group: IAdminSecAdvJobs }) {
  const id = React.useId();
  const label = JOB_TYPE_LABEL[group.jobType];
  return (
    <section aria-labelledby={id} className="grid gap-2">
      <h3 id={id} className="font-medium text-[var(--color-royal-blue)]">
        {label ?? <code>{group.jobType}</code>}
        {label && (
          <span className="ml-2 font-mono text-xs font-normal text-muted-foreground">
            {group.jobType}
          </span>
        )}
      </h3>
      {group.latest.length === 0 ? (
        <p className="text-muted-foreground">No job of this type is on the queue.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>Run after</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Completed</TableHead>
                <TableHead>Last error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {group.latest.map((job) => (
                <JobRow key={job.jobId} job={job} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}

function JobRow({ job }: { job: IAdminSecAdvJob }) {
  return (
    <TableRow>
      <TableCell className="font-mono text-xs">{job.jobId}</TableCell>
      <TableCell className="min-w-48">
        <JobStatus job={job} />
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {job.attempts} of {job.maxAttempts}
      </TableCell>
      <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
        {formatDateTime(job.runAfter)}
      </TableCell>
      <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
        {formatDateTime(job.createdAt)}
      </TableCell>
      <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
        {job.completedAt ? formatDateTime(job.completedAt) : '—'}
      </TableCell>
      <TableCell className="min-w-64">
        {job.lastError ? (
          <pre className="font-mono text-xs whitespace-pre-wrap wrap-anywhere">{job.lastError}</pre>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * A job's status in words. The queue has no `failed` on purpose, so two of its states need a
 * sentence the word does not carry: a `queued` job that has been attempted failed and will be
 * tried again, and a `dead` one is out of attempts and will not be — loud, since nothing but a
 * person moves it. A status survey-api adds later is shown as it spells it.
 */
function JobStatus({ job }: { job: IAdminSecAdvJob }) {
  switch (job.status) {
    case 'queued':
      return (
        <span className="grid justify-items-start gap-1">
          <Badge variant="outline">Queued</Badge>
          {job.attempts > 0 && (
            <span className="text-xs text-muted-foreground">
              Failed before — will be tried again.
            </span>
          )}
        </span>
      );
    case 'running':
      return <Badge variant="soft">Running</Badge>;
    case 'succeeded':
      return <Badge variant="success">Succeeded</Badge>;
    case 'dead':
      return (
        <span className="grid justify-items-start gap-1">
          <Badge variant="destructive">Dead</Badge>
          <span className="text-xs text-destructive">
            Out of attempts — never retried by itself.
          </span>
        </span>
      );
    default:
      return <Badge variant="outline">{String(job.status)}</Badge>;
  }
}

/** This app's names for survey-api's two import job types (`JobTypes`). A type it adds later
 *  is shown as it spells it. */
const JOB_TYPE_LABEL: Record<string, string> = {
  sec_adv_firm_import: 'Daily firm feed',
  sec_adv_filing_import: 'Monthly filing archives'
};

// ---------------------------------------------------------------------------
// The import log
// ---------------------------------------------------------------------------

function ImportTable({
  page,
  filtered,
  onPage
}: {
  page: IPage<IAdminSecAdvImport>;
  filtered: boolean;
  onPage: (page: number) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Kind</TableHead>
            <TableHead>File</TableHead>
            <TableHead>Describes</TableHead>
            <TableHead>Rows read</TableHead>
            <TableHead>Rows skipped</TableHead>
            <TableHead>Imported</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {page.items.map((entry) => (
            <TableRow key={`${entry.kind}|${entry.fileName}|${entry.importedAt}`}>
              <TableCell className="whitespace-nowrap">
                {KIND_LABEL[entry.kind] ?? <code>{entry.kind}</code>}
              </TableCell>
              <TableCell className="font-mono text-xs">{entry.fileName}</TableCell>
              <TableCell className="whitespace-nowrap">
                {entry.kind === 'filing'
                  ? formatMonth(entry.generatedOn)
                  : formatDay(entry.generatedOn)}
              </TableCell>
              <TableCell>{entry.rowCount}</TableCell>
              <TableCell>{entry.skippedCount}</TableCell>
              <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                {formatDateTime(entry.importedAt)}
              </TableCell>
            </TableRow>
          ))}
          {page.items.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                {page.total > 0 ? (
                  <PastTheEnd
                    page={page.page}
                    pageSize={page.pageSize}
                    total={page.total}
                    counted={['import', 'imports']}
                    onPage={onPage}
                  />
                ) : filtered ? (
                  'No import of this kind has been logged.'
                ) : (
                  'No import has been logged.'
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

/** The log's three positions — `kind` absent, then survey-api's two `SecAdvImportKinds`. */
const KINDS = [
  { value: '', label: 'All' },
  { value: 'firm', label: 'Firm feed' },
  { value: 'filing', label: 'Filing archives' }
];

const KIND_LABEL: Record<string, string> = { firm: 'Firm feed', filing: 'Filing archive' };

/** A page of the log, with the filter it answers. */
interface IAnsweredLog {
  asked: string;
  filtered: boolean;
  page: IPage<IAdminSecAdvImport>;
}
